import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import type { AvailabilityException, AvailabilityRule, ConsultationDay, PageProps } from "@/types/inertia"

type Props = PageProps & {
  rules: AvailabilityRule[]
  exceptions: AvailabilityException[]
  preview_days: ConsultationDay[]
  slot_booking_enabled: boolean
}

const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
// Monday-first display order; day_of_week values stay 0=Sunday..6=Saturday.
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]

function minuteLabel(minute: number) {
  const h = Math.floor(minute / 60)
  const m = minute % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const MINUTE_OPTIONS = Array.from({ length: 24 * 4 + 1 }, (_, i) => i * 15)

function MinuteSelect({
  id,
  value,
  onChange,
}: {
  id: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <Select id={id} value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {MINUTE_OPTIONS.map((m) => (
        <option key={m} value={m}>
          {minuteLabel(m)}
        </option>
      ))}
    </Select>
  )
}

function AddWindowRow({ day }: { day: number }) {
  const [start, setStart] = React.useState(9 * 60)
  const [end, setEnd] = React.useState(17 * 60)
  const [processing, setProcessing] = React.useState(false)

  function add() {
    setProcessing(true)
    router.post(
      "/admin/availability_rules",
      { availability_rule: { day_of_week: day, start_minute: start, end_minute: end } },
      { onFinish: () => setProcessing(false) }
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <MinuteSelect id={`add-start-${day}`} value={start} onChange={setStart} />
      <span className="text-ink-muted">–</span>
      <MinuteSelect id={`add-end-${day}`} value={end} onChange={setEnd} />
      <Button variant="secondary" size="sm" onClick={add} disabled={processing}>
        + Add window
      </Button>
    </div>
  )
}

export default function AvailabilityShow() {
  const { props } = usePage<Props>()
  const { rules, exceptions, preview_days, slot_booking_enabled } = props
  const errors = props.errors ?? {}
  const flash = props.flash

  const [blockDate, setBlockDate] = React.useState("")
  const [blockReason, setBlockReason] = React.useState("")
  const [blockProcessing, setBlockProcessing] = React.useState(false)

  function addException(e: React.FormEvent) {
    e.preventDefault()
    setBlockProcessing(true)
    router.post(
      "/admin/availability_exceptions",
      { availability_exception: { date: blockDate, reason: blockReason } },
      {
        onFinish: () => setBlockProcessing(false),
        onSuccess: () => {
          setBlockDate("")
          setBlockReason("")
        },
      }
    )
  }

  const totalSlots = preview_days.reduce((sum, d) => sum + d.slots.length, 0)

  return (
    <AdminShell>
      <Head title="Availability">
        <meta name="description" content="Manage weekly consultation availability and blocked dates." />
        <meta property="og:title" content="Availability" />
        <meta property="og:description" content="Manage weekly consultation availability and blocked dates." />
      </Head>

      <PageHeader
        title="Availability"
        description="Weekly windows for free 15-minute consultations (UK time). Clients book from these on Gisela's /book page."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {errors.base && <p className="mt-4 text-sm text-danger-display">{errors.base}</p>}
      {errors.end_minute && <p className="mt-4 text-sm text-danger-display">End time {errors.end_minute}</p>}

      {/* Feature switch: whether /book shows the slot picker at all */}
      <div className="mt-6 rounded-lg border border-hairline bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Switch
            checked={slot_booking_enabled}
            onChange={(enabled) =>
              router.patch(
                "/admin/features/consultation_slot_booking",
                { enabled },
                { preserveScroll: true }
              )
            }
            label="Online slot booking on /book"
          />
          <Badge tone={slot_booking_enabled ? "accent" : "neutral"}>
            {slot_booking_enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
        <p className="mt-2 text-sm text-ink-muted">
          While disabled, /book leads with ring-now and the request-a-callback form — no time picker.
          The weekly windows below are kept and take effect again the moment this is switched on.
        </p>
      </div>

      {/* Weekly grid */}
      <div className="mt-6 overflow-hidden rounded-lg border border-hairline">
        {DAY_ORDER.map((day) => {
          const dayRules = rules.filter((r) => r.day_of_week === day)
          return (
            <div key={day} className="flex flex-col gap-3 border-b border-hairline bg-white px-4 py-4 last:border-b-0 sm:flex-row sm:items-start">
              <div className="w-28 shrink-0 pt-1.5 text-sm font-medium text-ink-body">{DAY_LABELS[day]}</div>
              <div className="flex-1 space-y-2">
                {dayRules.length === 0 && <p className="pt-1.5 text-sm text-ink-muted">Unavailable</p>}
                {dayRules.map((rule) => (
                  <div key={rule.id} className="flex flex-wrap items-center gap-2">
                    <span className="rounded-lg bg-surface px-3 py-1.5 text-sm tabular-nums text-ink-body">
                      {minuteLabel(rule.start_minute)} – {minuteLabel(rule.end_minute)}
                    </span>
                    {!rule.active && <Badge tone="neutral">Paused</Badge>}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        router.patch(`/admin/availability_rules/${rule.id}`, {
                          availability_rule: { active: !rule.active },
                        })
                      }
                    >
                      {rule.active ? "Pause" : "Resume"}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-danger-display hover:border-danger hover:bg-danger-faded"
                      onClick={() => router.delete(`/admin/availability_rules/${rule.id}`)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <AddWindowRow day={day} />
              </div>
            </div>
          )
        })}
      </div>

      {/* Blocked dates */}
      <div className="mt-10">
        <h2>Blocked dates</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Whole days with no consultations — holidays, training days, and so on.
        </p>

        <form onSubmit={addException} className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="block-date">Date</label>
            <Input
              id="block-date"
              type="date"
              required
              value={blockDate}
              onChange={(e) => setBlockDate(e.target.value)}
              aria-invalid={!!errors.date}
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="block-reason">
              Reason <span className="font-normal text-ink-muted">(optional)</span>
            </label>
            <Input
              id="block-reason"
              type="text"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              className="mt-1"
              placeholder="Holiday"
            />
          </div>
          <Button type="submit" variant="secondary" disabled={blockProcessing}>
            Block date
          </Button>
        </form>
        {errors.date && <p className="mt-2 text-sm text-danger-display">Date {errors.date}</p>}

        {exceptions.length > 0 && (
          <ul className="mt-4 space-y-2">
            {exceptions.map((exception) => (
              <li key={exception.id} className="flex items-center gap-3 rounded-lg border border-hairline bg-white px-4 py-2 text-sm">
                <span className="tabular-nums text-ink-body">{exception.date}</span>
                {exception.reason && <span className="text-ink-muted">{exception.reason}</span>}
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  onClick={() => router.delete(`/admin/availability_exceptions/${exception.id}`)}
                >
                  Unblock
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Live preview */}
      <div className="mt-10">
        <h2>What clients see</h2>
        <p className="mt-1 text-sm text-ink-muted">
          {totalSlots} open slot{totalSlots === 1 ? "" : "s"} over the next 4 weeks (24h minimum notice, UK time).
        </p>
        <div className="mt-4 space-y-3">
          {preview_days.length === 0 && (
            <p className="text-sm text-ink-muted">
              No bookable slots — add a weekly window above to open availability.
            </p>
          )}
          {preview_days.slice(0, 7).map((day) => (
            <div key={day.date} className="rounded-lg border border-hairline bg-white px-4 py-3">
              <p className="text-sm font-medium text-ink-body">{day.day_label}</p>
              <p className="mt-1 text-sm tabular-nums text-ink-muted">
                {day.slots.map((s) => s.london_label).join(" · ")}
              </p>
            </div>
          ))}
          {preview_days.length > 7 && (
            <p className="text-sm text-ink-muted">…and {preview_days.length - 7} more days.</p>
          )}
        </div>
      </div>
    </AdminShell>
  )
}
